// Height decision for the sandboxed HTML-visual iframe. Kept as a pure module
// (no component export) so it is unit-testable and HMR-friendly.

export const MIN_HEIGHT = 80;
// A full answer can be tall; allow plenty so the whole document shows without an
// inner scrollbar, but cap it as a runaway guard.
export const MAX_HEIGHT = 24000;
// A few px of slack so a sub-pixel content height never forces an inner scrollbar.
export const HEIGHT_PAD = 4;
// Runaway guard: a growth loop (e.g. content sized in viewport units that couples to
// the iframe height) ratchets up in tiny consecutive steps. After this many small
// consecutive increases we stop growing; a large jump (real reflow) or any shrink
// resets the count, so genuine content is never clipped.
export const RATCHET_STEP_PX = 16;
export const MAX_RATCHET_STEPS = 6;

// Returns the next iframe height + updated ratchet count, or null to ignore the
// report (unchanged, or a frozen growth loop).
export function nextVisualHeight(
  current: number,
  reported: number,
  growth: number,
): { height: number; growth: number } | null {
  const next = Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, reported + HEIGHT_PAD));
  if (next === current) return null;
  if (next > current) {
    if (next - current <= RATCHET_STEP_PX) {
      if (growth >= MAX_RATCHET_STEPS) return null; // looks like a growth loop → freeze
      return { height: next, growth: growth + 1 };
    }
    return { height: next, growth: 0 }; // big jump = real reflow
  }
  return { height: next, growth: 0 }; // shrink = real reflow
}
